import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOptimizedQueryKey } from "@/hooks/useOptimizedQueryKey";
import { useAuthReady } from "@/hooks/useAuthReady";
import { isValidUuid } from "@/lib/uuidValidation";
import { ensureAuthenticatedSession } from "@/lib/authRetry";
import { ensureMembershipForCurrentUser, getErrorCode, getErrorMessage } from "@/lib/supabase/rpc-helpers";
import type { Database } from "@/integrations/supabase/types";
import { logger } from "@/lib/logger";

export interface FormInvitation {
  id: string;
  recipient_email: string;
  subject: string;
  message: string;
  status: "pending" | "viewed" | "completed" | "expired";
  due_date: string | null;
  created_at: string;
  invite_token: string;
  organization_id: string;
  form_template_id: string | null;
  assignee_id: string | null;
  // V2 fields
  batch_id: string | null;
  batch_label: string | null;
  purpose: string | null;
  source: string | null;
  requested_scope: Record<string, unknown> | null;
  viewed_at: string | null;
  completed_at: string | null;
  // V2.1 observability fields
  delivery_channel: string | null;
  recipient_identity: "existing_user" | "guest" | "household_linked" | null;
  household_linked: boolean;
  started_at: string | null;
  expired_at: string | null;
  link_expires_at: string | null;
  scope_requested_now: string[] | null;
  scope_deferred: string[] | null;
  scope_satisfied: string[] | null;
  scope_missing: string[] | null;
  stall_signals: Array<Record<string, unknown>> | null;
  form_templates: {
    title: string;
    description: string | null;
  } | null;
  form_responses?: Array<{
    id: string;
    form_data: Record<string, unknown>;
    status: string;
    submitted_at: string;
  }>;
}

export interface InvitationQueryFilters {
  status?: string | null;
  templateId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  searchEmail?: string | null;
  assigneeId?: string | null;
  batchLabel?: string | null;
  lifecycleState?: string | null; // "viewed_not_completed", "started_not_completed"
  stallReason?: string | null;
  limit?: number;
  offset?: number;
}

export async function fetchFormInvitations(
  organizationId: string,
  userId: string,
  filters: InvitationQueryFilters = {}
): Promise<FormInvitation[]> {
  // Check session but don't block if check fails - let RPC fail naturally with proper error
  const hasSession = await ensureAuthenticatedSession();
  if (!hasSession) {
    console.debug('[fetchFormInvitations] No valid session detected');
    // Don't return empty - throw to trigger error state in UI
    throw new Error('Authentication required');
  }

  const {
    status = null,
    templateId = null,
    startDate = null,
    endDate = null,
    searchEmail = null,
    assigneeId = null,
    batchLabel = null,
    lifecycleState = null,
    stallReason = null,
    limit = 100,
    offset = 0,
  } = filters;

  // Special nil UUID used to filter for unassigned invitations
  const UNASSIGNED_UUID = '00000000-0000-0000-0000-000000000000';
  
  // Use secure RPC to fetch invitations with proper RLS and org role checks
  const callRpc = () => supabase.rpc("get_paginated_invitations", {
    p_org_id: organizationId,
    p_limit: limit,
    p_offset: offset,
    p_status: status || null,
    p_template_id: templateId || null,
    p_start_date: startDate || null,
    p_end_date: endDate || null,
    p_search_email: searchEmail || null,
    p_assignee_id: assigneeId === 'unassigned' ? UNASSIGNED_UUID : (assigneeId || null),
  });

  let { data, error } = await callRpc();

  if (error) {
    const msg = getErrorMessage(error);
    const code = getErrorCode(error);

    // Self-heal: if org membership record is missing, repair once and retry.
    if (code === 'P0001' && msg.includes('organization membership required')) {
      const { error: repairError } = await ensureMembershipForCurrentUser();
      if (!repairError) {
        const retry = await callRpc();
        data = retry.data;
        error = retry.error;
      }
    }

    if (error) {
      logger.error('[fetchFormInvitations] RPC error:', error instanceof Error ? error : new Error(String(error)), {
        hook: "useFormInvitationsQuery",
        action: "fetch_invitations",
        errorCode: getErrorCode(error)
      });
      throw error;
    }
  }
  // Map RPC response to FormInvitation format (type-safe)
  type InvitationRpcRow = Database['public']['Functions']['get_paginated_invitations']['Returns'][number];
  
  const invitations: FormInvitation[] = (data ?? []).map((item: InvitationRpcRow) => ({
    id: item.id,
    recipient_email: item.recipient_email,
    subject: item.subject,
    message: item.message || '',
    status: item.status as "pending" | "viewed" | "completed" | "expired",
    due_date: item.due_date,
    created_at: item.created_at,
    invite_token: item.invite_token,
    organization_id: organizationId,
    form_template_id: item.form_template_id,
    assignee_id: item.assignee_id || null,
    // V2 fields - cast as any for now since RPC may not return them yet
    batch_id: (item as Record<string, unknown>).batch_id as string | null ?? null,
    batch_label: (item as Record<string, unknown>).batch_label as string | null ?? null,
    purpose: (item as Record<string, unknown>).purpose as string | null ?? null,
    source: (item as Record<string, unknown>).source as string | null ?? null,
    requested_scope: (item as Record<string, unknown>).requested_scope as Record<string, unknown> | null ?? null,
    viewed_at: (item as Record<string, unknown>).viewed_at as string | null ?? null,
    completed_at: (item as Record<string, unknown>).completed_at as string | null ?? null,
    delivery_channel: (item as Record<string, unknown>).delivery_channel as string | null ?? null,
    recipient_identity: (item as Record<string, unknown>).recipient_identity as FormInvitation["recipient_identity"] ?? null,
    household_linked: Boolean((item as Record<string, unknown>).household_linked),
    started_at: (item as Record<string, unknown>).started_at as string | null ?? null,
    expired_at: (item as Record<string, unknown>).expired_at as string | null ?? null,
    link_expires_at: (item as Record<string, unknown>).link_expires_at as string | null ?? null,
    scope_requested_now: (item as Record<string, unknown>).scope_requested_now as string[] | null ?? null,
    scope_deferred: (item as Record<string, unknown>).scope_deferred as string[] | null ?? null,
    scope_satisfied: (item as Record<string, unknown>).scope_satisfied as string[] | null ?? null,
    scope_missing: (item as Record<string, unknown>).scope_missing as string[] | null ?? null,
    stall_signals: (item as Record<string, unknown>).stall_signals as Array<Record<string, unknown>> | null ?? null,
    form_templates: item.template_title ? {
      title: item.template_title,
      description: item.template_description || null
    } : null
  }));

  // Apply client-side filters for new observability fields
  let filteredInvitations = invitations;

  // Filter by batch label
  if (batchLabel) {
    filteredInvitations = filteredInvitations.filter(
      inv => inv.batch_label === batchLabel
    );
  }

  // Filter by lifecycle state
  if (lifecycleState === 'viewed_not_completed') {
    filteredInvitations = filteredInvitations.filter(
      inv => inv.viewed_at && inv.status !== 'completed'
    );
  } else if (lifecycleState === 'started_not_completed') {
    filteredInvitations = filteredInvitations.filter(
      inv => inv.started_at && inv.status !== 'completed'
    );
  }

  // Filter by stall reason
  if (stallReason) {
    filteredInvitations = filteredInvitations.filter(inv => {
      if (!inv.stall_signals || inv.stall_signals.length === 0) return false;
      return inv.stall_signals.some(signal => {
        if (typeof signal === 'string') return signal === stallReason;
        return (signal as Record<string, unknown>).reason === stallReason;
      });
    });
  }

  return filteredInvitations;
}

export function useFormInvitationsQuery(filters: InvitationQueryFilters = {}) {
  const { organizationId, userId } = useOptimizedQueryKey();
  const { isReady, hasAccessToken } = useAuthReady();

  // CRITICAL: Validate IDs are proper UUIDs before using in query key
  const validOrgId = isValidUuid(organizationId) ? organizationId : undefined;
  const validUserId = isValidUuid(userId) ? userId : undefined;

  // Stable filter key to prevent unnecessary re-fetches
  const filterKey = useMemo(() => JSON.stringify(filters), [filters]);

  const queryKey = useMemo(
    () => ["org-invitations", validOrgId ?? "no-org", validUserId ?? "no-user", filterKey] as const,
    [validOrgId, validUserId, filterKey]
  );

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // Double-check UUIDs are valid before calling RPC
      if (!validOrgId || !validUserId) {
        console.debug('[useFormInvitationsQuery] Skipping fetch - invalid IDs', { validOrgId, validUserId });
        return [] as FormInvitation[];
      }

      return fetchFormInvitations(validOrgId, validUserId, filters);
    },
    // CRITICAL: Only enable when we have valid UUIDs - prevents 400 errors
    enabled: isReady && hasAccessToken && !!validOrgId && !!validUserId,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 10,
  });

  return {
    ...query,
    queryKey,
  };
}
